import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  ClipboardDocumentListIcon,
  CalendarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Task } from '../../types';
import { tasksAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  onTaskUpdate?: (task: Task) => void;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  task, 
  onTaskUpdate 
}) => {
  const [taskHistory, setTaskHistory] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && task) {
      loadTaskHistory();
    }
  }, [isOpen, task]);

  const loadTaskHistory = async () => {
    if (!task) return;
    
    try {
      setIsLoading(true);
      const history = await tasksAPI.getTaskHistory(task.id);
      setTaskHistory(history);
    } catch (error) {
      console.error('Error loading task history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'TODO':
        return 'bg-gray-100 text-gray-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const isOverdue = task?.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'DONE';

  if (!task) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Background overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal Container */}
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl z-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className={`w-4 h-4 rounded-full ${getPriorityColor(task.priority)}`} />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{task.title}</h2>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                      <span>Created by {task.creator.firstName} {task.creator.lastName}</span>
                      <span>•</span>
                      <span>{new Date(task.createdAt).toLocaleDateString()}</span>
                      {isOverdue && (
                        <>
                          <span>•</span>
                          <span className="text-red-600 font-medium flex items-center">
                            <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                            Overdue
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Description</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      {task.description ? (
                        <p className="text-gray-700 whitespace-pre-wrap">{task.description}</p>
                      ) : (
                        <p className="text-gray-500 italic">No description provided</p>
                      )}
                    </div>
                  </div>

                  {/* Assignees */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Assignees</h3>
                    {task.assignees.length === 0 ? (
                      <p className="text-gray-500">No assignees</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {task.assignees.map((assignee) => (
                          <div key={assignee.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {assignee.user.firstName[0]}{assignee.user.lastName[0]}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {assignee.user.firstName} {assignee.user.lastName}
                              </p>
                              <p className="text-sm text-gray-500">{assignee.user.username}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* History */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Activity</h3>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner size="md" />
                      </div>
                    ) : taskHistory ? (
                      <div className="space-y-3">
                        {taskHistory.history.map((entry: any, index: number) => (
                          <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-2 h-2 bg-primary-600 rounded-full mt-2" />
                            <div>
                              <p className="text-sm font-medium text-gray-900 capitalize">
                                {entry.action}
                              </p>
                              <p className="text-sm text-gray-600">{entry.details}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(entry.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No activity history available</p>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  {/* Status & Priority */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Status & Priority</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(task.status)}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Priority</span>
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
                          <span className="text-sm font-medium text-gray-900">{task.priority}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Team */}
                  {task.team && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                        <UserGroupIcon className="w-4 h-4 mr-2" />
                        Team
                      </h4>
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: task.team.color }}
                        />
                        <span className="text-sm font-medium text-gray-900">{task.team.name}</span>
                      </div>

                    </div>
                  )}

                  {/* Dates */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Dates
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Created</span>
                        <span className="text-gray-900">{new Date(task.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Updated</span>
                        <span className="text-gray-900">{new Date(task.updatedAt).toLocaleDateString()}</span>
                      </div>
                      {task.dueDate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Due Date</span>
                          <span className={`font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                            {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Task ID */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Task ID</h4>
                    <code className="text-xs text-gray-600 bg-white px-2 py-1 rounded border">
                      {task.id}
                    </code>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TaskDetailModal;
